import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search, Package, Calendar, Clock, FileText as FileInvoice, CreditCard, PackageOpen, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { orderService } from '../../services/orderService';
import { notificationService } from '../../services/notificationService';
import { uploadImage } from '../../services/cloudinaryService';
import { supabase } from '../../lib/supabase';
import { OrderWithDetails } from '../../types/order';
import ConfirmDialog from '../ui/ConfirmDialog';
import DocumentViewer from '../ui/DocumentViewer';

// Exportar para que sea considerado un módulo
export {};

interface OrderListProps {
  onAdd: () => void;
  onEdit: (order: OrderWithDetails) => void;
  onGenerateSale?: (order: OrderWithDetails) => void;
}

const OrderList = ({ onAdd, onEdit, onGenerateSale }: OrderListProps) => {
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<OrderWithDetails | null>(null);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ url: string; name: string } | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'Todas', color: 'bg-gray-100 text-gray-800' },
    { value: 'generated', label: 'Generadas', color: 'bg-blue-100 text-blue-800' },
    { value: 'delivered', label: 'Entregadas', color: 'bg-green-100 text-green-800' },
    { value: 'invoiced', label: 'Facturadas', color: 'bg-orange-100 text-orange-800' },
    { value: 'paid', label: 'Pagadas', color: 'bg-purple-100 text-purple-800' }
  ];

  const statusIcons = {
    generated: <Clock className="w-4 h-4" />,
    delivered: <PackageOpen className="w-4 h-4" />,
    invoiced: <FileInvoice className="w-4 h-4" />,
    paid: <CreditCard className="w-4 h-4" />
  };

  useEffect(() => {
    loadOrders();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      let data: OrderWithDetails[] = [];
      if (statusFilter === 'all') {
        data = await orderService.getOrders();
      } else {
        const allOrders = await orderService.getOrders();
        data = allOrders.filter(order => order.status === statusFilter);
      }
      
      setOrders(data);
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
      notificationService.error('Error al cargar órdenes', 'No se pudieron cargar las órdenes. Por favor, recargue la página.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const handleDeleteOrder = async (order: OrderWithDetails) => {
    setOrderToDelete(order);
    setShowDeleteDialog(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      const success = await orderService.deleteOrder(orderToDelete.id);
      if (success) {
        notificationService.success('Orden eliminada', 'La orden ha sido eliminada correctamente.');
        loadOrders(); // Recargar la lista
      } else {
        // Verificar si hay ventas asociadas para mostrar un mensaje más específico
        const { data: associatedSales } = await supabase
          .from('sales')
          .select('sale_number, status')
          .eq('order_id', orderToDelete.id)
          .limit(1);

        if (associatedSales && associatedSales.length > 0) {
          const sale = associatedSales[0];
          notificationService.error(
            'No se puede eliminar la orden',
            `Esta orden tiene una venta asociada (${sale.sale_number}). Para eliminar la orden, primero debe eliminar la venta asociada.`
          );
        } else {
          notificationService.error('Error al eliminar orden', 'No se pudo eliminar la orden. Por favor, intente nuevamente.');
        }
      }
    } catch (error) {
      console.error('Error al eliminar orden:', error);
      notificationService.error('Error al eliminar orden', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setShowDeleteDialog(false);
      setOrderToDelete(null);
    }
  };

  const handleViewDocument = (order: OrderWithDetails) => {
    if (order.order_document_url) {
      setSelectedDocument({
        url: order.order_document_url,
        name: order.order_document || 'Documento de orden'
      });
      setShowDocumentDialog(true);
    }
  };

  const handleUploadInvoice = async (order: OrderWithDetails) => {
    if (!invoiceFile) {
      notificationService.error('Error', 'Por favor seleccione un archivo de factura');
      return;
    }

    setUploadingInvoice(true);
    try {
      // Usar el servicio de órdenes para facturar (actualizar estado y guardar factura)
      const result = await orderService.invoiceOrder(order.id, invoiceFile);
      
      if (result.success) {
        notificationService.success('Factura subida', 'La factura se ha subido correctamente y la orden ha sido marcada como facturada.');
        // Recargar la lista de órdenes para mostrar los cambios
        loadOrders();
      } else {
        notificationService.error('Error', result.error || 'No se pudo facturar la orden.');
      }
    } catch (error) {
      console.error('Error al subir factura:', error);
      notificationService.error('Error', 'No se pudo subir la factura. Por favor intente nuevamente.');
    } finally {
      setUploadingInvoice(false);
      setInvoiceFile(null);
      setShowInvoiceDialog(false);
    }
  };

  const handleViewInvoice = (order: OrderWithDetails) => {
    if (order.invoice_document_url) {
      setSelectedDocument({
        url: order.invoice_document_url,
        name: order.invoice_document || 'Factura'
      });
      setShowDocumentDialog(true);
    }
  };

  const handleStatusUpdate = async (orderId: number, newStatus: OrderWithDetails['status']) => {
    console.log('🔄 Actualizando estado de orden:', { orderId, newStatus });
    
    // Si el nuevo estado es "invoiced", mostrar el modal para subir factura
    if (newStatus === 'invoiced') {
      const order = orders.find(o => o.id === orderId);
      if (order) {
        setSelectedOrder(order);
        setShowInvoiceDialog(true);
        return; // No actualizar el estado hasta que se suba la factura
      }
    }

    setUpdatingStatus(orderId);
    try {
      console.log('🔄 Llamando a orderService.updateOrderStatus con:', { orderId, newStatus });
      const updatedOrder = await orderService.updateOrderStatus(orderId, newStatus);
      console.log('📊 Respuesta de orderService.updateOrderStatus:', updatedOrder);
      
      if (updatedOrder) {
        notificationService.success('Estado actualizado', 'El estado de la orden ha sido actualizado correctamente.');
        loadOrders(); // Recargar la lista
      } else {
        notificationService.error('Error al actualizar estado', 'No se pudo actualizar el estado. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      notificationService.error('Error al actualizar estado', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setUpdatingStatus(null);
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Asegurarse de que la fecha se interprete en la zona horaria local
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Lima'
    });
  };

  const getNextStatus = (currentStatus: string): OrderWithDetails['status'] | null => {
    switch (currentStatus) {
      case 'generated':
        return 'delivered';
      case 'delivered':
        return 'invoiced';
      case 'invoiced':
        return 'paid';
      case 'paid':
        return null;
      default:
        return null;
    }
  };

  const getNextStatusText = (currentStatus: string): string => {
    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) return '';
    
    switch (nextStatus) {
      case 'delivered':
        return 'Marcar como Entregado';
      case 'invoiced':
        return 'Marcar como Facturado';
      case 'paid':
        return 'Marcar como Pagado';
      default:
        return '';
    }
  };

  const getNextStatusIcon = (currentStatus: string) => {
    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) return null;
    
    switch (nextStatus) {
      case 'delivered':
        return <PackageOpen className="w-4 h-4" />;
      case 'invoiced':
        return <FileInvoice className="w-4 h-4" />;
      case 'paid':
        return <CreditCard className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Órdenes de Pedido</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Total: {orders.length} órdenes</span>
          <Button onClick={onAdd} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Orden
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <button
              key={status.value}
              onClick={() => setStatusFilter(status.value)}
              className={`px-3 py-2 sm:px-4 rounded-lg font-medium transition-colors text-sm ${
                statusFilter === status.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
        
        {/* Búsqueda */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por número, cliente o producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Orders List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredOrders.length === 0 ? (
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-sm border text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all' ? 'No se encontraron órdenes con los filtros aplicados' : 'No hay órdenes registradas'}
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                className={`bg-white p-4 sm:p-6 rounded-lg shadow-sm border cursor-pointer transition-all hover:shadow-md ${
                  selectedOrder?.id === order.id ? 'border-primary-500 ring-2 ring-primary-200' : ''
                }`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">{order.order_number}</h3>
                    <p className="text-sm text-gray-600 truncate">{order.customer_name}</p>
                    <p className="text-sm text-gray-600 truncate">{order.product.name}</p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <span className={`px-2 py-1 sm:px-3 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(order.status)}`}>
                      {statusIcons[order.status as keyof typeof statusIcons] || statusIcons.generated}
                      <span className="hidden sm:inline">{getStatusLabel(order.status)}</span>
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    <span className="hidden sm:inline">
                      {formatDate(order.order_date)}
                    </span>
                    <span className="sm:hidden">
                      {new Date(order.order_date).toLocaleDateString('es-ES')}
                    </span>
                    <span className="ml-3">
                      {formatCurrency(order.total_with_igv)}
                    </span>
                  </div>
                  <div className="flex space-x-1 sm:space-x-2">
                    {/* Botón para cambiar estado */}
                    {getNextStatus(order.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusUpdate(order.id, getNextStatus(order.status)!);
                        }}
                        disabled={updatingStatus === order.id}
                        className="text-green-600 hover:text-green-800 hover:bg-green-50 min-h-[38px] min-w-[38px] p-2 sm:p-1"
                        title={getNextStatusText(order.status)}
                      >
                        {updatingStatus === order.id ? (
                          <div className="w-4 h-4 animate-spin rounded-full border-b-2 border-green-600"></div>
                        ) : (
                          getNextStatusIcon(order.status)
                        )}
                        <span className="sr-only">{getNextStatusText(order.status)}</span>
                      </Button>
                    )}

                    {/* Botón para ver factura (solo en estado invoiced o paid) */}
                    {(order.status === 'invoiced' || order.status === 'paid') && order.invoice_document_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewInvoice(order);
                        }}
                        className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 min-h-[38px] min-w-[38px] p-2 sm:p-1"
                        title="Ver factura"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="sr-only">Ver factura</span>
                      </Button>
                    )}
                   
                    {/* Ver documento */}
                    {order.order_document_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDocument(order);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 min-h-[38px] min-w-[38px] p-2 sm:p-1"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="sr-only">Ver documento</span>
                      </Button>
                    )}
                     
                    {/* Editar */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(order);
                      }}
                      className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 min-h-[38px] min-w-[38px] p-2 sm:p-1"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="sr-only">Editar</span>
                    </Button>
                     
                    {/* Eliminar */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteOrder(order);
                      }}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 min-h-[38px] min-w-[38px] p-2 sm:p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="sr-only">Eliminar</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Order Details - Solo visible en desktop */}
        <div className="hidden lg:block">
          {selectedOrder ? (
            <div className="bg-white p-6 rounded-lg shadow-sm border max-h-[calc(100vh-12rem)] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalles de Orden</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Número de Orden</label>
                  <p className="text-gray-900">{selectedOrder.order_number}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Cliente</label>
                  <p className="text-gray-900">{selectedOrder.customer_name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Producto</label>
                  <p className="text-gray-900">{selectedOrder.product.name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Cantidad</label>
                  <p className="text-gray-900">{selectedOrder.quantity}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Precio Unitario</label>
                  <p className="text-gray-900">{formatCurrency(selectedOrder.unit_price)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Subtotal</label>
                  <p className="text-gray-900">{formatCurrency(selectedOrder.subtotal)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">IGV ({selectedOrder.igv_percentage}%)</label>
                  <p className="text-gray-900">{formatCurrency(selectedOrder.igv_amount)}</p>
                </div>
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">Total</label>
                    <p className="text-lg font-bold text-primary-600">
                      {formatCurrency(selectedOrder.total_with_igv)}
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Término de Pago</label>
                  <p className="text-gray-900">{selectedOrder.payment_terms}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Tiempo de Entrega</label>
                  <p className="text-gray-900">{selectedOrder.delivery_time}</p>
                </div>
                
                {selectedOrder.notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Notas</label>
                    <p className="text-gray-900">{selectedOrder.notes}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Fecha de Orden</label>
                  <p className="text-gray-900">{formatDate(selectedOrder.order_date)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Estado</label>
                  <div className="flex items-center space-x-2 mt-1">
                    {statusIcons[selectedOrder.status as keyof typeof statusIcons]}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedOrder.status)}`}>
                      {getStatusLabel(selectedOrder.status)}
                    </span>
                  </div>
                </div>

                {/* Mostrar información de la factura si existe */}
                {selectedOrder.invoice_document_url && (
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <label className="text-sm font-medium text-gray-700">Factura</label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewInvoice(selectedOrder)}
                      className="mt-2 w-full text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Factura
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm border text-center">
              <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Selecciona una orden para ver los detalles</p>
            </div>
          )}
        </div>
      </div>

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={confirmDeleteOrder}
        title="Eliminar Orden"
        message={`¿Está seguro de que desea eliminar la orden ${orderToDelete?.order_number}? Esta acción no se puede deshacer y el stock del producto será devuelto.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Visor de documentos */}
      <DocumentViewer
        isOpen={showDocumentDialog}
        onClose={() => setShowDocumentDialog(false)}
        documentUrl={selectedDocument?.url || ''}
        documentName={selectedDocument?.name || ''}
      />

      {/* Diálogo para subir factura */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 ${showInvoiceDialog ? 'block' : 'hidden'}`}>
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedOrder?.status === 'delivered' ? 'Subir Factura' : 'Facturar Orden'}
            </h3>
            <button
              onClick={() => {
                setShowInvoiceDialog(false);
                setInvoiceFile(null);
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="space-y-4">
              {selectedOrder?.status === 'delivered' && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Para marcar esta orden como facturada, debe subir el archivo de la factura en formato PDF o imagen.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar archivo de factura
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setInvoiceFile(file);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              {invoiceFile && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    Archivo seleccionado: <span className="font-medium">{invoiceFile.name}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Tamaño: {(invoiceFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => {
                setShowInvoiceDialog(false);
                setInvoiceFile(null);
              }}
              className="px-6 py-2"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (selectedOrder) {
                  handleUploadInvoice(selectedOrder);
                }
              }}
              disabled={!invoiceFile || uploadingInvoice}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {uploadingInvoice ? (
                <div className="w-4 h-4 animate-spin rounded-full border-b-2 border-white mr-2"></div>
              ) : null}
              {selectedOrder?.status === 'delivered' ? 'Subir y Facturar' : 'Subir Factura'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderList;